import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useAuth } from '@/hooks/useAuth';
import { LogOut, User, Menu, Home, ListTree, Calendar, CalendarPlus, Users, LayoutDashboard, Briefcase, Layers, Tag, Heart, Search } from 'lucide-react';

// Helper function for smooth scrolling
const scrollToSection = (sectionId: string) => {
  const element = document.getElementById(sectionId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

export const Navbar = () => {
  const { user, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const getUserInitials = () => {
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user?.email?.charAt(0).toUpperCase() || 'U';
  };

  const isClient = !!user && user.user_metadata?.user_type === 'client';
  const isProvider = !!user && user.user_metadata?.user_type === 'provider';

  const handleScrollLink = (sectionId: string) => {
    if (location.pathname !== '/') {
      // Navigate to home first, then scroll
      navigate('/');
      setTimeout(() => scrollToSection(sectionId), 100);
    } else {
      scrollToSection(sectionId);
    }
  };

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-luxury-gold/20 bg-luxury-pearl/95 backdrop-blur supports-[backdrop-filter]:bg-luxury-pearl/90">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        {/* Logo */}
        <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
          <span className="text-2xl font-bold tracking-tight">
            <span className="text-[#B8860B]">Beauty</span>
            <span className="text-[#D4AF37]">Book</span>
            <span className="text-[#B8860B] text-lg">AI</span>
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/"
            className={`flex items-center gap-2 text-sm font-medium transition-colors hover:text-[#B8860B] ${
              location.pathname === '/'
                ? 'text-[#B8860B]'
                : 'text-primary-900'
            }`}
          >
            <Home className="h-4 w-4" />
            Inicio
          </Link>
          {!isClient && (
            <button
              onClick={() => handleScrollLink('funcionalidades')}
              className="flex items-center gap-2 text-sm font-medium transition-colors hover:text-[#B8860B] text-primary-900"
            >
              <Layers className="h-4 w-4" />
              Funcionalidades
            </button>
          )}
          {!isClient && (
            <Link
              to="/empleo"
              className={`flex items-center gap-2 text-sm font-medium transition-colors hover:text-[#B8860B] ${
                location.pathname === '/empleo'
                  ? 'text-[#B8860B]'
                  : 'text-primary-900'
              }`}
            >
              <Briefcase className="h-4 w-4" />
              Empleo
            </Link>
          )}
          {!isClient && (
            <button
              onClick={() => handleScrollLink('precios')}
              className="flex items-center gap-2 text-sm font-medium transition-colors hover:text-[#B8860B] text-primary-900"
            >
              <Tag className="h-4 w-4" />
              Precios
            </button>
          )}
          {isClient && (
            <Link
              to="/providers"
              className={`flex items-center gap-2 text-sm font-medium transition-colors hover:text-[#B8860B] ${
                location.pathname === '/providers'
                  ? 'text-[#B8860B]'
                  : 'text-primary-900'
              }`}
            >
              <Search className="h-4 w-4" />
              Explorar
            </Link>
          )}
          {isClient && (
            <Link
              to="/client-dashboard"
              className={`flex items-center gap-2 text-sm font-medium transition-colors hover:text-[#B8860B] ${
                location.pathname === '/client-dashboard'
                  ? 'text-[#B8860B]'
                  : 'text-primary-900'
              }`}
            >
              <LayoutDashboard className="h-4 w-4" />
              Mi Panel
            </Link>
          )}
          {isClient && (
            <Link
              to="/reservas"
              className={`flex items-center gap-2 text-sm font-medium transition-colors hover:text-[#B8860B] ${
                location.pathname === '/reservas'
                  ? 'text-[#B8860B]'
                  : 'text-primary-900'
              }`}
            >
              <CalendarPlus className="h-4 w-4" />
              Nueva Reserva
            </Link>
          )}
          {isProvider && (
            <Link
              to="/dashboard"
              className={`flex items-center gap-2 text-sm font-medium transition-colors hover:text-[#B8860B] ${
                location.pathname === '/dashboard'
                  ? 'text-[#B8860B]'
                  : 'text-primary-900'
              }`}
            >
              <LayoutDashboard className="h-4 w-4" />
              Dashboard
            </Link>
          )}
          {isClient && (
            <Link
              to="/my-bookings"
              className={`flex items-center gap-2 text-sm font-medium transition-colors hover:text-[#B8860B] ${
                location.pathname === '/my-bookings'
                  ? 'text-[#B8860B]'
                  : 'text-primary-900'
              }`}
            >
              <Calendar className="h-4 w-4" />
              Mis Reservas
            </Link>
          )}
          {isClient && (
            <Link
              to="/client/favorites"
              className={`flex items-center gap-2 text-sm font-medium transition-colors hover:text-[#B8860B] ${
                location.pathname === '/client/favorites'
                  ? 'text-[#B8860B]'
                  : 'text-primary-900'
              }`}
            >
              <Heart className="h-4 w-4" />
              Favoritos
            </Link>
          )}
        </div>

        {/* Desktop Auth */}
        <div className="hidden md:flex items-center gap-4">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                  <Avatar className="h-10 w-10 border-2 border-luxury-gold">
                    <AvatarImage src={user.user_metadata?.avatar_url} />
                    <AvatarFallback className="bg-gradient-to-br from-luxury-gold to-luxury-bronze text-primary-900 font-semibold">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-background z-50" align="end">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">
                      {user.user_metadata?.full_name || 'Usuario'}
                    </p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isProvider && (
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="cursor-pointer">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                )}
                {isClient && (
                  <DropdownMenuItem asChild>
                    <Link to="/client-dashboard" className="cursor-pointer">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Mi Panel
                    </Link>
                  </DropdownMenuItem>
                )}
                {isClient && (
                  <DropdownMenuItem asChild>
                    <Link to="/my-bookings" className="cursor-pointer">
                      <Calendar className="mr-2 h-4 w-4" />
                      Mis Reservas
                    </Link>
                  </DropdownMenuItem>
                )}
                {isClient && (
                  <DropdownMenuItem asChild>
                    <Link to="/client/favorites" className="cursor-pointer">
                      <Heart className="mr-2 h-4 w-4" />
                      Favoritos
                    </Link>
                  </DropdownMenuItem>
                )}
                {isProvider && (
                  <DropdownMenuItem asChild>
                    <Link to="/workers" className="cursor-pointer">
                      <Users className="mr-2 h-4 w-4" />
                      Mis Trabajadores
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} className="cursor-pointer text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-primary-900 hover:text-[#B8860B]">
                  Iniciar Sesión
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-luxury-gold hover:bg-luxury-bronze text-primary-900 font-semibold">
                  Registrarse
                </Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile Menu */}
        <div className="md:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-background">
              <div className="flex flex-col gap-4 mt-8">
                {user && (
                  <div className="flex items-center gap-3 pb-4 border-b border-luxury-gold/20">
                    <Avatar className="h-12 w-12 border-2 border-luxury-gold">
                      <AvatarImage src={user.user_metadata?.avatar_url} />
                      <AvatarFallback className="bg-gradient-to-br from-luxury-gold to-luxury-bronze text-primary-900 font-semibold">
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col flex-1">
                      <p className="text-sm font-medium">
                        {user.user_metadata?.full_name || 'Usuario'}
                      </p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                )}

                <Link
                  to="/"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    location.pathname === '/'
                      ? 'bg-[#FDF6E3] text-[#B8860B]'
                      : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Home className="h-5 w-5" />
                  <span className="font-medium">Inicio</span>
                </Link>

                {!isClient && (
                  <button
                    onClick={() => {
                      setMobileOpen(false);
                      handleScrollLink('funcionalidades');
                    }}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-muted-foreground hover:bg-muted text-left"
                  >
                    <Layers className="h-5 w-5" />
                    <span className="font-medium">Funcionalidades</span>
                  </button>
                )}

                {!isClient && (
                  <Link
                    to="/empleo"
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                      location.pathname === '/empleo'
                        ? 'bg-[#FDF6E3] text-[#B8860B]'
                        : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <Briefcase className="h-5 w-5" />
                    <span className="font-medium">Empleo</span>
                  </Link>
                )}

                {!isClient && (
                  <button
                    onClick={() => {
                      setMobileOpen(false);
                      handleScrollLink('precios');
                    }}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-muted-foreground hover:bg-muted text-left"
                  >
                    <Tag className="h-5 w-5" />
                    <span className="font-medium">Precios</span>
                  </button>
                )}

                {isClient && (
                  <Link
                    to="/providers"
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                      location.pathname === '/providers'
                        ? 'bg-[#FDF6E3] text-[#B8860B]'
                        : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <Search className="h-5 w-5" />
                    <span className="font-medium">Explorar</span>
                  </Link>
                )}

                {user && (
                  <>
                    {isClient && (
                      <Link
                        to="/client-dashboard"
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                          location.pathname === '/client-dashboard'
                            ? 'bg-[#FDF6E3] text-[#B8860B]'
                            : 'text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        <LayoutDashboard className="h-5 w-5" />
                        <span className="font-medium">Mi Panel</span>
                      </Link>
                    )}
                    {isClient && (
                      <Link
                        to="/reservas"
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                          location.pathname === '/reservas'
                            ? 'bg-[#FDF6E3] text-[#B8860B]'
                            : 'text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        <CalendarPlus className="h-5 w-5" />
                        <span className="font-medium">Nueva Reserva</span>
                      </Link>
                    )}
                    {isProvider && (
                      <Link
                        to="/dashboard"
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                          location.pathname === '/dashboard'
                            ? 'bg-[#FDF6E3] text-[#B8860B]'
                            : 'text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        <LayoutDashboard className="h-5 w-5" />
                        <span className="font-medium">Dashboard</span>
                      </Link>
                    )}
                    {isClient && (
                      <Link
                        to="/my-bookings"
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                          location.pathname === '/my-bookings'
                            ? 'bg-[#FDF6E3] text-[#B8860B]'
                            : 'text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        <Calendar className="h-5 w-5" />
                        <span className="font-medium">Mis Reservas</span>
                      </Link>
                    )}
                    {isClient && (
                      <Link
                        to="/client/favorites"
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                          location.pathname === '/client/favorites'
                            ? 'bg-[#FDF6E3] text-[#B8860B]'
                            : 'text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        <Heart className="h-5 w-5" />
                        <span className="font-medium">Favoritos</span>
                      </Link>
                    )}
                    {isProvider && (
                      <Link
                        to="/workers"
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                          location.pathname.startsWith('/workers')
                            ? 'bg-[#FDF6E3] text-[#B8860B]'
                            : 'text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        <Users className="h-5 w-5" />
                        <span className="font-medium">Mis Trabajadores</span>
                      </Link>
                    )}
                  </>
                )}

                <div className="pt-4 border-t mt-auto">
                  {user ? (
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => {
                        signOut();
                        setMobileOpen(false);
                      }}
                    >
                      <LogOut className="h-5 w-5" />
                      Cerrar Sesión
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <Link to="/login" onClick={() => setMobileOpen(false)}>
                        <Button variant="outline" className="w-full">
                          Iniciar Sesión
                        </Button>
                      </Link>
                      <Link to="/register" onClick={() => setMobileOpen(false)}>
                        <Button className="w-full bg-luxury-gold hover:bg-luxury-bronze text-primary-900 font-semibold">
                          Registrarse
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};
